import { CloseIcon } from "@chakra-ui/icons";
import { Box, IconButton, Text, Tooltip, useToast } from "@chakra-ui/react";
import { useContext, useEffect, useMemo, useState } from "react";
import Draggable from "react-draggable";
import { AiOutlineExpand, AiOutlineCompress } from "react-icons/ai";
import { RequestPolicy } from "urql";
import { Coordinate } from "~/generated/graphql.types";
import { HyperlapsePlayer } from "~/routes/_shared/HyperlapsePlayer";
import { EditingHyperlapseContext, EditModeContext } from "../..";
import { useLatestHyperlapseQuery } from "./index.generated";

export const useLatestHyperlapseImages = (courseId: string) => {
  const queryContext = useMemo(
    () => ({ requestPolicy: "network-only" as RequestPolicy }),
    []
  );
  const [latestHyperlapseSpotRes, latestHyperlapseSpot] =
    useLatestHyperlapseQuery({
      variables: { courseId: courseId },
      pause: true,
      context: queryContext,
    });

  const toast = useToast();
  const { switchHyperlapsePlayer } = useContext(EditModeContext);

  useEffect(() => {
    latestHyperlapseSpot();
  }, []);

  useEffect(() => {
    if (
      !latestHyperlapseSpotRes.stale &&
      latestHyperlapseSpotRes.data?.latestHyperlapse === null
    ) {
      toast({
        title: "このコースには登録済み道の風景動画がありません。",
        description: (
          <>
            <Text>以下の手順に従って道の風景動画を登録してください</Text>
            <Text>1. コンテンツ設定モードにする</Text>
            <Text>
              2.
              マップを右クリックして道の風景動画生成用スポットを複数個登録する
            </Text>
            <Text>3. 右上の 道の風景動画を表示 をクリックする</Text>
          </>
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      switchHyperlapsePlayer(false);
    }
  }, [latestHyperlapseSpotRes]);

  const latestHyperlapseImages =
    latestHyperlapseSpotRes.data?.latestHyperlapse?.hyperlapse!
      .imageInfomations;
  return latestHyperlapseImages;
};

export const DraggableHyperlapsePlayer = ({
  courseId,
  onChangeFrame,
}: {
  courseId: string;
  onChangeFrame?: (frame: {
    i: number;
    coordinate: Coordinate;
    heading: number;
  }) => void;
}) => {
  const { switchHyperlapsePlayer } = useContext(EditModeContext);
  const { setHyperlapseFrame } = useContext(EditingHyperlapseContext);
  const [hyperlapsePlayerSize, setHyperlapsePlayerSize] = useState<
    "small" | "large"
  >("small");

  const hyperlapseImages = useLatestHyperlapseImages(courseId);
  return hyperlapseImages ? (
    <Draggable>
      <Box
        w={hyperlapsePlayerSize === "large" ? "800px" : "260px"}
        h={hyperlapsePlayerSize === "large" ? "840px" : "300px"}
        position="absolute"
        zIndex={400}
        bgColor={"#3D6BAB"}
      >
        <>
          <Tooltip label="">
            <IconButton
              size="lg"
              position="absolute"
              top={2}
              right={2}
              color="white"
              aria-label="expand player"
              variant="ghost"
              icon={<CloseIcon />}
              onClick={() => {
                switchHyperlapsePlayer(false);
              }}
            />
          </Tooltip>

          {hyperlapsePlayerSize === "large" ? (
            <Tooltip label="プレイヤーを小さくする">
              <IconButton
                size="lg"
                position="absolute"
                top={2}
                left={2}
                color="white"
                aria-label="expand player"
                variant="ghost"
                icon={<AiOutlineCompress />}
                onClick={() => setHyperlapsePlayerSize("small")}
              />
            </Tooltip>
          ) : (
            <Tooltip label="プレイヤーを大きくする">
              <IconButton
                size="lg"
                position="absolute"
                top={2}
                left={2}
                color="white"
                variant="ghost"
                aria-label="shrink player"
                icon={<AiOutlineExpand />}
                onClick={() => setHyperlapsePlayerSize("large")}
              />
            </Tooltip>
          )}
          <HyperlapsePlayer
            images={hyperlapseImages}
            onChangeFrame={setHyperlapseFrame}
          />
        </>
      </Box>
    </Draggable>
  ) : null;
};
